import { useContext } from 'react';
import { Theme } from '../theme';
import { ThemeContext, ThemeContextInterface, ThemeMode } from './ThemeProvider';

/**
 * Theme Utility Functions
 *
 * This file provides utility functions for working with the theme.
 * It follows Module M7 (Integration Points) principles by providing
 * standardized ways to access theme values.
 *
 * It also follows Module M15 (Error Handling) principles by implementing
 * defensive coding practices and providing fallback mechanisms.
 */

/**
 * Custom hook to use the theme context
 *
 * @returns The theme context value
 * @throws Error if used outside a ThemeProvider
 */
export const useTheme = (): ThemeContextInterface => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Custom hook to toggle theme mode
 *
 * @returns A function to toggle between light and dark mode
 */
export const useThemeToggle = (): () => void => {
  const { toggleMode } = useTheme();
  return toggleMode;
};

/**
 * Custom hook to access theme values
 *
 * @returns The current theme object
 */
export const useThemeValues = (): Theme => {
  const { theme } = useTheme();
  return theme as unknown as Theme;
};

/**
 * Custom hook to access and set theme mode
 *
 * @returns An object with the current mode and a function to set the mode
 */
export const useThemeMode = (): { mode: ThemeMode; setMode: (mode: ThemeMode) => void } => {
  const { mode, setMode } = useTheme();
  return { mode, setMode };
};

/**
 * Get a theme value safely with fallback
 * 
 * This function provides a type-safe way to access theme values with fallbacks.
 * It uses optional chaining and nullish coalescing to handle missing values.
 * 
 * @param theme The theme object or undefined
 * @param path A function that accesses the desired theme property
 * @param fallback A fallback value to use if the theme property is undefined
 * @returns The theme property value or the fallback
 * 
 * @example
 * // Get primary color with fallback
 * const primaryColor = getThemeValue(theme, t => t.colors.primary[500], '#1890FF');
 */
export function getThemeValue<T>(
  theme: Theme | undefined,
  path: (theme: Theme) => T,
  fallback: T
): T {
  try {
    if (!theme) return fallback;
    const value = path(theme);
    return value !== undefined && value !== null ? value : fallback;
  } catch (error) {
    console.error('Error accessing theme value:', error);
    return fallback;
  }
}

/**
 * Get a CSS variable value with fallback
 * 
 * This function provides a way to access CSS variables with fallbacks.
 * It's useful for styled-components that need to access theme values.
 * 
 * @param variableName The CSS variable name (without the -- prefix)
 * @param fallback A fallback value to use if the CSS variable is undefined
 * @returns The CSS variable value or the fallback
 * 
 * @example
 * // Get primary color with fallback
 * const primaryColor = getCSSVariable('primary-500', '#1890FF');
 */
export function getCSSVariable(variableName: string, fallback: string): string {
  return `var(--${variableName}, ${fallback})`;
}

/**
 * Get the global theme object
 * 
 * This function provides a way to access the global theme object.
 * It's useful for styled-components that need to access theme values
 * outside of the ThemeProvider context.
 * 
 * @returns The global theme object or undefined
 */
export function getGlobalTheme(): Theme | undefined {
  if (typeof window !== 'undefined') {
    return window.__THEME_DATA__;
  }
  return undefined;
}

/**
 * Create a theme-aware styled component
 * 
 * This function provides a way to create styled components that
 * access theme values safely, even if the theme context is missing.
 * 
 * @param getStyles A function that returns styles based on the theme
 * @returns A function that returns styles with fallbacks
 * 
 * @example
 * // Create a styled component with theme awareness
 * const StyledButton = styled.button`
 *   ${themeAware(theme => `
 *     background-color: ${getThemeValue(theme, t => t.colors.primary[500], '#1890FF')};
 *     color: ${getThemeValue(theme, t => t.colors.theme.text.primary, '#FFFFFF')};
 *   `)}
 * `;
 */
export function themeAware(getStyles: (theme: Theme) => string): (props: { theme?: Theme }) => string {
  return (props: { theme?: Theme }) => {
    const theme = props.theme || getGlobalTheme();
    return getStyles(theme as Theme);
  };
}

// All utilities are exported directly above